const select = require('../select/select');

const selectSearchable = Object.assign({}, select, {
    subelements: {
        mainHolder: false,
        inputHolder: false,
        displayInput: false,
        optionsMenuTrigger: false,
        optionsMenu: false,
        searchInput: false,
        optionsHolder: false,
        restingIcon: false,
        typingIcon: false,
        searchingIcon: false,
    },
    selectedIndex: -1,
    prefilterComponentIds: {},
    remoteOptions: false,
    preloadDefaultOptions: true,
    searchMinLength: 3,
    // Cached submenu adapter instance (resolved via hm.behaviors.submenu.get()
    // on init). Generic name — under metronic_8 the adapter wraps KTMenu,
    // under bootstrap5 it would wrap bootstrap.Dropdown.
    submenu: false,
    searchMode: false,
    stashedOptionElements: false,

    init: function () {
        select.init.call(this);

        this.subelements.searchInput.addEventListener('keyup', (e) => { this.startSearch(); });

        this.initOptionsMenu();
    },

    inited: function () {
        select.inited.call(this);

        if (this.selectedValue) {
            this.setSelected(this.selectedValue);
        }
    },

    getOptionElements: function () {
        return this.subelements.optionsHolder.querySelectorAll('[data-zit-option-for="' + this.id + '"]');
    },

    getValue: function () {
        if (this.isHidden && this.hiddenValue !== false) {
            return this.hiddenValue;
        }

        return this.selectedValue;
    },

    getOptionId: function (optionElement) {
        return optionElement.dataset.zitOptionId;
    },

    getOptionText: function (optionElement) {
        // Title span carries data-zit-subelement-type="optionTitle"; the class
        // resolves via the tier's cssClassesMap (menu-title under m8, "" under
        // zb5) so we cannot query by class here.
        return optionElement.querySelector('[data-zit-subelement-type="optionTitle"]').innerHTML;
    },

    removeOption: function (option) {
        if (this.selectedValue === this.getOptionId(option._element)) {
            //the selected value is the one that is being removed
            this.selectedValue = '';
            this.setSelectedIndex(0); //set the new selected value to the first option
        }

        this.removeActive(option._element);
        option._element.remove();

        return true;
    },

    // Returns the clickable link element inside an option. Uses the per-option
    // subelement marker (data-zit-subelement-type="optionLink") rather than a
    // tag-name or class-name query so the DOM is tier-portable.
    getOptionLink: function (optionElement) {
        return optionElement.querySelector('[data-zit-subelement-type="optionLink"]');
    },

    removeActive: function (optionElement) {
        // activation.stateClass('active') returns the tier-specific class string
        // (single 'active' under both zb5 and m8 today, multi-class under future
        // tiers). Split + spread so classList accepts each token individually.
        let activeClass = this.hm.behaviors.activation.stateClass('active');
        let activeTokens = activeClass.split(' ');
        let linkEl = this.getOptionLink(optionElement);
        if (linkEl.classList.contains(activeTokens[0])) {
            linkEl.classList.remove(...activeTokens);
        }
    },

    addActive: function (optionElement) {
        let activeClass = this.hm.behaviors.activation.stateClass('active');
        let activeTokens = activeClass.split(' ');
        let linkEl = this.getOptionLink(optionElement);
        if (!linkEl.classList.contains(activeTokens[0])) {
            linkEl.classList.add(...activeTokens);
        }
    },

    addOption: function (option) {
        this.insertOptionElement(option._element);

        return true;
    },

    setSelectedIndex: function (selectedIndex) {
        let optionElements = this.getOptionElements();

        if (optionElements.length === 0) {
            selectedIndex = -1;
        }

        optionElements.forEach((optionElement) => {
            this.removeActive(optionElement);
        });

        let changeHappened = false;

        if (selectedIndex >= 0) {
            this.addActive(optionElements[selectedIndex]);
            let newSelectedValue = this.getOptionId(optionElements[selectedIndex]);

            if (newSelectedValue !== this.selectedValue) {
                this.selectedValue = newSelectedValue;
                changeHappened = true; //selectedINdex might remain the same but options might change
            }

            this.subelements.displayInput.value = this.getOptionText(optionElements[selectedIndex]);
        } else if (this.remoteOptions && !this.preloadDefaultOptions) {
            //leave the selectedValue and displayInput.value alone
        } else {
            this.selectedValue = '';
            this.subelements.displayInput.value = '';
        }

        this.element.value = this.selectedValue;

        if (this.selectedIndex !== selectedIndex) {
            this.selectedIndex = selectedIndex;
            changeHappened = true;
        }

        if (changeHappened) {
            let ev = new Event('change');
            this.element.dispatchEvent(ev);
        }
    },

    initOptionsMenu: function () {
        let width = this.subelements.displayInput.offsetWidth;

        this.subelements.optionsMenu.style.minWidth = width + 'px';

        // Bulk-init any submenu markup on the page (idempotent). Needed in
        // case the optionsMenu was inserted dynamically after the first
        // theme-level init pass.
        this.hm.behaviors.submenu.createInstances();

        // Resolve the submenu adapter for this component's optionsMenu element.
        // Under metronic_8 this wraps KTMenu; under bootstrap5 it wraps
        // bootstrap.Dropdown (with Bootstrap-shaped markup expectations).
        this.submenu = this.hm.behaviors.submenu.get(this.subelements.optionsMenu);

        this.subelements.displayInput.addEventListener('focus', (e) => { this.showOptionsMenu(); });
        this.subelements.optionsMenuTrigger.addEventListener('click', (e) => { e.preventDefault(); this.showOptionsMenu(); });

        // Subscribe to the submenu's lifecycle via neutral event names; the
        // adapter maps to the underlying framework's namespaced event
        // (kt.menu.dropdown.shown under m8, shown.bs.dropdown under BS).
        this.submenu.on('shown', (element) => { this.optionsMenuOpened(); });

        this.submenu.on('hidden', (element) => { this.optionsMenuClosed(); });

        this.initOptionElements();
    },

    initOptionElements: function () {
        let optionElements = this.getOptionElements();

        // Wire click on each option's link element (resolved via the optionLink
        // subelement marker — see getOptionLink for the rationale).
        optionElements.forEach((optionElement) => {
            this.getOptionLink(optionElement).addEventListener('click', (e) => {
                e.preventDefault();
                this.setSelected(this.getOptionId(optionElement));
            });
        });
    },

    showOptionsMenu: function () {
        // Anchor the submenu against the inputHolder element and open it.
        // Metronic_8's KTMenu supports anchoring against an arbitrary element;
        // bootstrap5's bootstrap.Dropdown adapter currently ignores the
        // targetEl and anchors to its own toggle (documented divergence).
        this.submenu.show(this.subelements.inputHolder);
        this.subelements.searchInput.focus();
    },

    optionsMenuOpened: function () {
        this.subelements.searchInput.focus();
        this.searchMode = true;

        if (this.searchMinLength === 0) {
            this.doSearch();
        }
    },

    optionsMenuClosed: function () {
        let optionElements = this.getOptionElements();

        let selectedIndex = this.findOptionIndex(this.selectedValue);
        let selectedText = '';
        if (selectedIndex >= 0) {
            selectedText = this.getOptionText(optionElements[selectedIndex]);
        }

        this.subelements.displayInput.value = selectedText;

        this.searchMode = false;
    },

    startSearch: function () {
        if (this.searchMode) {
            this.toggleSearchVisual('typing');
            this.startTimer('search', () => { this.doSearch(); }, 800);
        }
    },

    doSearch: async function () {
        if (this.searchMode) {
            let searchQuery = this.subelements.searchInput.value;

            if (searchQuery.length >= this.searchMinLength || searchQuery.length === 0) {
                this.toggleSearchVisual('searching');
                if (this.remoteOptions) {
                    await this.fetchOptions(searchQuery);
                } else {
                    if (this.stashedOptionElements === false) {
                        this.stashedOptionElements = this.getOptionElements();
                    }

                    this.subelements.optionsHolder.innerHTML = '';

                    searchQuery = searchQuery.toLowerCase();
                    let regExSearchQuery = false;

                    if (searchQuery.indexOf('*') > -1 || searchQuery.indexOf('%') > -1 || searchQuery.indexOf('+') > -1 || searchQuery.indexOf('?') > -1) {
                        regExSearchQuery = searchQuery;
                        regExSearchQuery = regExSearchQuery.replaceAll('%', '.*');
                        regExSearchQuery = regExSearchQuery.replaceAll('*', '.*');
                        regExSearchQuery = regExSearchQuery.replaceAll('+', '.+');
                        regExSearchQuery = regExSearchQuery.replaceAll('?', '.{1}');
                        regExSearchQuery = '^' + regExSearchQuery + '$';
                        regExSearchQuery = new RegExp(regExSearchQuery);
                    }

                    this.stashedOptionElements.forEach((optionElement) => {
                        let optionText = this.getOptionText(optionElement);

                        let addOption = false;

                        if (searchQuery.length === 0) {
                            addOption = true;
                        } else {
                            if (regExSearchQuery !== false) {
                                //wildcards are used
                                if (optionText.toLowerCase().match(regExSearchQuery)) {
                                    addOption = true;
                                }
                            } else {
                                //no wildcards
                                if (optionText.toLowerCase().includes(searchQuery)) {
                                    addOption = true;
                                }
                            }
                        }

                        if (addOption) {
                            this.insertOptionElement(optionElement);
                        }
                    });

                    this.toggleSearchVisual('resting');
                }
            } else {
                this.toggleSearchVisual('resting');
            }
        }

        this.clearTimer('search');
    },

    fetchOptions: async function (searchQuery) {
        let data = {
            configHash: this.configHash,
            componentAction: 'remoteOptions',
            searchQuery: searchQuery
        };

        for (let attributeName in this.prefilterComponentIds) {
            let componentId = this.prefilterComponentIds[attributeName];

            if (typeof this.hm.app.components[componentId] !== typeof undefined) {
                data[attributeName] = this.hm.app.components[componentId].getValue();
            }
        }

        let response = await this.hm.app.fetchJson('components', 'main', data);

        if (typeof response.options !== typeof undefined) {
            this.subelements.optionsHolder.innerHTML = '';

            let optionHTML = this.getTemplate('option');
            //create new optionElements
            response.options.forEach((option) => {
                let newOptionComponent = this.hm.helper.string2element(optionHTML);
                newOptionComponent.dataset.zitOptionId = option.id;
                newOptionComponent.dataset.zitExtraAttributes = JSON.stringify(option);
                // Write the option name into the title span located via its
                // subelement marker (class names are tier-resolved and may be
                // empty under zb5, so a class query would not be portable).
                newOptionComponent.querySelector('[data-zit-subelement-type="optionTitle"]').innerHTML = option.name;
                this.subelements.optionsHolder.append(newOptionComponent);
            });

            this.initOptions();
            this.initOptionElements();

            for (let attribute in this.filteredBy) {
                this.filterHide(attribute, String(this.filteredBy[attribute]));
            }

            this.toggleSearchVisual('resting');
        }
    },

    insertOptionElement: function (optionElement) {
        this.subelements.optionsHolder.append(optionElement);
    },

    toggleSearchVisual: function (toState) {
        ['resting', 'typing', 'searching'].forEach((state) => {
            if (state === toState) {
                this.hm.helper.showElement(this.subelements[state + 'Icon']);
            } else {
                this.hm.helper.hideElement(this.subelements[state + 'Icon']);
            }
        });
    }

});

module.exports = selectSearchable;